import type { IChallenge } from "@bbj-types/challenge";
import { Alert, Collapse, Stack, Typography } from "@mui/material";
import { useState } from "react";
import type { TPaperCandidateChallengeObjective } from "../types";
import { PaperCandidateObjectiveButtonDelete } from "./buttons/delete";
import { PaperCandidateObjectiveButtonEdit } from "./buttons/edit";
import { PaperCandidateObjectiveForm } from "./form";
import { PaperCandidateObjectiveStatus } from "./status";
import { PaperCandidateObjectiveRoot, paperCandidateObjectiveClasses } from "./styles";
import { PaperCandidateObjectiveTitle } from "./title";

export enum CandidateObjectiveLoadingState {
  NONE = 0,
  EDIT = 1,
  EDIT_STATUS = 2,
  DELETE = 3,
}

export interface IPaperCandidateObjectiveProps<T extends TPaperCandidateChallengeObjective> {
  disabled?: boolean;
  challenge: Pick<IChallenge, "objectives">;
  candidateObjective: T;
  onEdit?: (candidateObjective: T) => void | Promise<void>;
  onDelete: (candidateObjective: T) => void | Promise<void>;
}

export const PaperCandidateObjective = <T extends TPaperCandidateChallengeObjective>({
  disabled,
  challenge,
  candidateObjective,
  onEdit,
  onDelete,
}: IPaperCandidateObjectiveProps<T>) => {
  const [loading, setLoading] = useState(CandidateObjectiveLoadingState.NONE);
  const [collapsed, setCollapsed] = useState(true);
  const currentObjective = challenge.objectives.find((objective) => objective._id === candidateObjective.objective);

  const handleSubmit = async (candidateObjective: T) => {
    if (!onEdit) {
      return;
    }
    setLoading(CandidateObjectiveLoadingState.EDIT);
    try {
      await onEdit(candidateObjective);
      setCollapsed(true);
    } finally {
      setLoading(CandidateObjectiveLoadingState.NONE);
    }
  };

  if (!currentObjective) {
    return (
      <Alert severity="error">
        <Typography variant="body1SemiBold">Oops !</Typography>
        <Typography variant="body1">Impossible de récupérer les informations de cet objectif.</Typography>
      </Alert>
    );
  }
  return (
    <PaperCandidateObjectiveRoot className={paperCandidateObjectiveClasses.root}>
      <Stack className={paperCandidateObjectiveClasses.header}>
        <PaperCandidateObjectiveTitle objective={currentObjective} candidateObjective={candidateObjective} />
        <Stack className={paperCandidateObjectiveClasses.headerActions}>
          {candidateObjective.status !== undefined && onEdit && (
            <PaperCandidateObjectiveStatus
              candidateObjective={candidateObjective}
              onEdit={onEdit}
              loading={loading}
              setLoading={setLoading}
            />
          )}
          {onEdit && (
            <PaperCandidateObjectiveButtonEdit setCollapsed={setCollapsed} disabled={disabled} loading={loading} />
          )}
          <PaperCandidateObjectiveButtonDelete
            candidateObjective={candidateObjective}
            onDelete={onDelete}
            disabled={disabled}
            loading={loading}
            setLoading={setLoading}
          />
        </Stack>
      </Stack>
      {onEdit && (
        <Collapse in={!collapsed}>
          <PaperCandidateObjectiveForm
            open={!collapsed}
            challenge={challenge}
            candidateObjective={candidateObjective}
            onSubmit={handleSubmit}
            onCancel={() => setCollapsed(true)}
          />
        </Collapse>
      )}
    </PaperCandidateObjectiveRoot>
  );
};
